import React from 'react';

// Subtle STEM-themed decorative icons for backgrounds
// Very low opacity (5-10%) to stay as subtle decoration

export const AtomIcon = ({ className = '', style = {} }) => (
  <svg 
    className={className}
    style={{ opacity: 0.06, ...style }}
    viewBox="0 0 100 100" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="50" cy="50" r="8" fill="#00E5FF" />
    <ellipse cx="50" cy="50" rx="40" ry="15" stroke="#00E5FF" strokeWidth="1" transform="rotate(0 50 50)" />
    <ellipse cx="50" cy="50" rx="40" ry="15" stroke="#00E5FF" strokeWidth="1" transform="rotate(60 50 50)" />
    <ellipse cx="50" cy="50" rx="40" ry="15" stroke="#00E5FF" strokeWidth="1" transform="rotate(120 50 50)" />
  </svg>
);

export const IntegralIcon = ({ className = '', style = {} }) => (
  <svg 
    className={className}
    style={{ opacity: 0.05, ...style }}
    viewBox="0 0 60 100" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M35 10 C45 10 50 20 50 35 L50 65 C50 80 45 90 35 90 C25 90 20 85 20 75" 
      stroke="#00E5FF" 
      strokeWidth="2" 
      fill="none"
    />
    <circle cx="35" cy="10" r="4" fill="#00E5FF" />
    <circle cx="25" cy="90" r="4" fill="#00E5FF" />
  </svg>
);

export const SigmaIcon = ({ className = '', style = {} }) => (
  <svg 
    className={className}
    style={{ opacity: 0.05, ...style }}
    viewBox="0 0 80 100" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M65 15 L20 15 L45 50 L20 85 L65 85" 
      stroke="#00E5FF" 
      strokeWidth="2" 
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const WaveIcon = ({ className = '', style = {} }) => (
  <svg 
    className={className}
    style={{ opacity: 0.06, ...style }}
    viewBox="0 0 200 60" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M0 30 Q25 5 50 30 T100 30 T150 30 T200 30" 
      stroke="#00E5FF" 
      strokeWidth="1.5" 
      fill="none"
    />
    <path 
      d="M0 40 Q25 15 50 40 T100 40 T150 40 T200 40" 
      stroke="#00E5FF" 
      strokeWidth="0.8" 
      fill="none"
      opacity="0.5"
    />
  </svg>
);

export const MoleculeIcon = ({ className = '', style = {} }) => (
  <svg 
    className={className}
    style={{ opacity: 0.06, ...style }}
    viewBox="0 0 100 100" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="50" cy="50" r="10" stroke="#00E5FF" strokeWidth="1.5" fill="none" />
    <circle cx="50" cy="50" r="3" fill="#00E5FF" />
    <circle cx="20" cy="30" r="6" stroke="#00E5FF" strokeWidth="1" fill="none" />
    <circle cx="20" cy="30" r="2" fill="#00E5FF" />
    <circle cx="80" cy="30" r="6" stroke="#00E5FF" strokeWidth="1" fill="none" />
    <circle cx="80" cy="30" r="2" fill="#00E5FF" />
    <circle cx="30" cy="80" r="6" stroke="#00E5FF" strokeWidth="1" fill="none" />
    <circle cx="30" cy="80" r="2" fill="#00E5FF" />
    <circle cx="70" cy="80" r="6" stroke="#00E5FF" strokeWidth="1" fill="none" />
    <circle cx="70" cy="80" r="2" fill="#00E5FF" />
    <line x1="43" y1="43" x2="26" y2="35" stroke="#00E5FF" strokeWidth="1" />
    <line x1="57" y1="43" x2="74" y2="35" stroke="#00E5FF" strokeWidth="1" />
    <line x1="43" y1="57" x2="35" y2="74" stroke="#00E5FF" strokeWidth="1" />
    <line x1="57" y1="57" x2="65" y2="74" stroke="#00E5FF" strokeWidth="1" />
  </svg>
);

export const FormulaIcon = ({ className = '', style = {} }) => (
  <svg 
    className={className}
    style={{ opacity: 0.04, ...style }}
    viewBox="0 0 120 40" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <text x="5" y="28" fontFamily="serif" fontSize="20" fill="#00E5FF">E = mc²</text>
  </svg>
);

// Background decorator component - place at section level
const StemBackgroundDecorations = ({ variant = 'default' }) => {
  const decorations = {
    default: (
      <>
        <AtomIcon 
          className="absolute w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40" 
          style={{ top: '10%', right: '5%', opacity: 0.04 }} 
        />
        <WaveIcon 
          className="absolute w-32 h-16 md:w-48 md:h-20 lg:w-64 lg:h-24" 
          style={{ bottom: '15%', left: '3%', opacity: 0.05 }} 
        />
      </>
    ),
    hero: (
      <>
        <AtomIcon 
          className="absolute w-32 h-32 md:w-48 md:h-48 lg:w-56 lg:h-56" 
          style={{ top: '5%', right: '8%', opacity: 0.05 }} 
        />
        <MoleculeIcon 
          className="absolute w-20 h-20 md:w-28 md:h-28 lg:w-36 lg:h-36" 
          style={{ bottom: '20%', left: '5%', opacity: 0.04 }} 
        />
      </>
    ),
    courses: (
      <>
        <SigmaIcon 
          className="absolute w-16 h-20 md:w-20 md:h-28 lg:w-24 lg:h-32" 
          style={{ top: '15%', left: '8%', opacity: 0.04 }} 
        />
        <IntegralIcon 
          className="absolute w-12 h-20 md:w-16 md:h-28 lg:w-20 lg:h-36" 
          style={{ bottom: '10%', right: '10%', opacity: 0.04 }} 
        />
      </>
    ),
    about: (
      <>
        <MoleculeIcon 
          className="absolute w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40" 
          style={{ top: '8%', right: '6%', opacity: 0.04 }} 
        />
        <WaveIcon 
          className="absolute w-40 h-16 md:w-56 md:h-20" 
          style={{ bottom: '12%', left: '5%', opacity: 0.05 }} 
        />
      </>
    ),
    results: (
      <>
        <FormulaIcon 
          className="absolute w-32 h-12 md:w-40 md:h-16" 
          style={{ top: '12%', right: '8%', opacity: 0.05 }} 
        />
        <AtomIcon 
          className="absolute w-20 h-20 md:w-28 md:h-28" 
          style={{ bottom: '18%', left: '6%', opacity: 0.04 }} 
        />
      </>
    ),
    minimal: (
      <AtomIcon 
        className="absolute w-20 h-20 md:w-28 md:h-28" 
        style={{ top: '50%', right: '5%', transform: 'translateY(-50%)', opacity: 0.03 }} 
      />
    )
  };

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {decorations[variant] || decorations.default}
    </div>
  );
};

export default StemBackgroundDecorations;
